import React, { useContext, useEffect, useState } from 'react';

import qs from 'qs';
import * as Yup from 'yup';
import i18n from 'i18n-js';
import { Formik, Field } from 'formik';
import { BastionContext } from 'context';
import { AuthLayout } from 'components/UI';
import { uiActions } from 'state/modules/UI';
import { AuthActions } from 'state/modules/auth';
import CheckIcon from '@mui/icons-material/Check';
import { TextField } from 'components/FormFields';
import appStore from 'assets/img/icons/app-store.png';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Box, Typography } from '@mui/material';
import googlePlay from 'assets/img/icons/google-play.png';
import { useLocation, useHistory } from 'react-router-dom';

const ChangePasswordPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const [firstLogin, setFirstLogin] = useState(false);
  const [isCustomer, setIsCustomer] = useState(false);

  const { loading, fetchChangePasswordSuccess } = useSelector((state) => state.auth);
  const { theme } = useContext(BastionContext);

  const ChangePasswordSchema = Yup.object().shape({
    password: Yup.string().required(i18n.t('common.required_field')),
    confirmPassword: Yup.string()
      .when('password', {
        is: (val) => (val && val.length > 0 ? true : false),
        then: Yup.string().oneOf([Yup.ref('password')], i18n.t('common.pwd_match')),
      })
      .required(i18n.t('common.required_field')),
  });

  const initialValues = {
    password: '',
    confirmPassword: '',
  };

  const parseBoolean = (value) => value === 'true';

  useEffect(() => {
    dispatch(AuthActions.fetchResetPasswordState());
  }, [dispatch]);

  useEffect(() => {
    const { token, first_login, is_customer } = qs.parse(location.search.substring(1));
    if (!token) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: i18n.t('auth.missing_token'),
        })
      );
    }
    if (parseBoolean(first_login)) {
      setFirstLogin(parseBoolean(first_login));
    }

    if (parseBoolean(is_customer)) {
      setIsCustomer(parseBoolean(is_customer));
    }
  }, [dispatch, location]);

  // Redirect after page refresh if the password was successfully changed
  useEffect(() => {
    if (sessionStorage.getItem('passwordChangeSuccess')) {
      sessionStorage.removeItem('passwordChangeSuccess');
      history.push('/login');
    }
  }, [history]);

  // When the password is successfully changed, set the flag in sessionStorage
  useEffect(() => {
    if (fetchChangePasswordSuccess && !isCustomer) {
      sessionStorage.setItem('passwordChangeSuccess', 'true');
    }
  }, [fetchChangePasswordSuccess, isCustomer]);

  return (
    <AuthLayout title={`${theme.page.title} | ${i18n.t('auth.change_password')}`}>
      {!fetchChangePasswordSuccess ? (
        <Box style={{ paddingTop: '25%' }} height="100%">
          <Formik
            initialValues={initialValues}
            validationSchema={ChangePasswordSchema}
            onSubmit={(values, { resetForm }) => {
              const { token } = qs.parse(location.search.substring(1));

              const payload = {
                password: values.password,
              };
              dispatch(AuthActions.fetchSetPassword(token, payload));
              window.scrollTo(0, 0);
              resetForm();
            }}
          >
            {({ handleSubmit }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Box mb={6}>
                  <Box mb={8}>
                    <Typography component="div">
                      <Box fontSize={16} lineHeight={1} fontWeight="bold" color="primary.main">
                        {i18n.t('auth.welcome_to', { name: '' })}
                      </Box>
                      <Box mt={2} fontSize={32} lineHeight={1} fontWeight="bold">
                        {theme.page.title}
                      </Box>
                      <Box
                        mt={1}
                        fontSize={24}
                        lineHeight={1}
                        letterSpacing={6.6}
                        fontFamily="Montserrat"
                      >
                        {theme.page.secondTitle}
                      </Box>
                    </Typography>
                  </Box>
                  {firstLogin ? (
                    <Typography component="div">
                      <Box fontSize={16}>{i18n.t('auth.change_password_set_text')}</Box>
                    </Typography>
                  ) : (
                    <Typography component="div">
                      <Box fontSize={16} mb={2}>
                        {i18n.t('auth.change_password_change_text')}
                      </Box>
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Box mt={1}>
                    <Field
                      type="password"
                      name="password"
                      component={TextField}
                      attrs={{
                        'data-cy': 'password-input',
                      }}
                      label={i18n.t('auth.password')}
                    />
                  </Box>
                  <Box mt={1}>
                    <Field
                      type="password"
                      name="confirmPassword"
                      component={TextField}
                      attrs={{
                        'data-cy': 'confirm-password-input',
                      }}
                      label={i18n.t('auth.confirm_password')}
                    />
                  </Box>
                </Box>
                <Box mt={6}>
                  <Button
                    fullWidth
                    type="submit"
                    color="primary"
                    disabled={loading}
                    loading={loading}
                    variant="contained"
                    sx={{ textTransform: 'none' }}
                  >
                    {firstLogin ? i18n.t('auth.set_password') : i18n.t('auth.reset_password')}
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      ) : (
        <Box
          height="100%"
          display="flex"
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
        >
          <Box
            width={100}
            height={100}
            display="flex"
            borderRadius="50%"
            alignItems="center"
            justifyContent="center"
            backgroundColor="#4D4F5C08"
          >
            <CheckIcon style={{ color: '#0181FF', fontSize: 60 }} />
          </Box>
          <Typography color="textSecondary" component="div" align="center">
            <Box fontSize={14} mt={2} component="p">
              {i18n.t('auth.change_password_success')}
            </Box>
            <Typography component="div" align="center" color="textSecondary" fontWeight="bold">
              <Box fontSize={14} mt={2} component="p">
                {i18n.t('auth.driver_app_download_prompt')}
              </Box>
            </Typography>
            <Box fontSize={14} mt={2} component="p">
              {i18n.t('auth.admin_login_prompt')}
            </Box>
            <Box mt={4} display="flex" justifyContent="center" sx={{ gap: '2rem' }}>
              <img src={googlePlay} alt="Google Play logo" />
              <img src={appStore} alt="App store logo" />
            </Box>
          </Typography>
        </Box>
      )}
    </AuthLayout>
  );
};

export default ChangePasswordPage;
